// core/services/firebase/interactionService.ts
import { 
  collection, 
  doc, 
  addDoc,
  setDoc,
  deleteDoc,
  getDoc,
  getDocs,
  query,
  where,
  orderBy,
  limit,
  Timestamp,
  increment,
  updateDoc
} from 'firebase/firestore';
import { db } from './config';
import { postService } from './postService';
import { socialService } from './socialService';
import { notificationService } from './notificationService';
import { counterService } from './counterService'; // Added
import { socialHelper } from '../../utils/socialHelper';

export interface Like {
  id: string;
  postId: string;
  userId: string;
  userHandle: string;
  createdAt: Date;
}

export interface Comment {
  id: string;
  postId: string;
  userId: string;
  userHandle: string;
  userName: string;
  userAvatar?: string;
  content: string;
  createdAt: Date;
}

export const interactionService = {
  /**
   * Like a post
   */
  async likePost(
    postId: string, 
    userId: string, 
    userHandle: string,
    userName: string,
    userAvatar?: string
  ): Promise<void> {
    const likeId = `${postId}_${userId}`;
    const likeRef = doc(db, 'likes', likeId);
    
    await setDoc(likeRef, {
      postId,
      userId,
      userHandle,
      createdAt: Timestamp.now()
    });

    // Increment post likes count using distributed counter extension
    await counterService.incrementCounter(`posts/${postId}`, 'likesCount', 1);

    // Create notification
    try {
      const post = await postService.getPostById(postId);
      if (post && post.authorId !== userHandle) {
        const authorProfile = await socialService.getProfileByHandle(post.authorId);
        if (authorProfile && authorProfile.userId) {
          await notificationService.createNotification(authorProfile.userId, {
            type: 'like',
            fromUserId: userId,
            fromUserHandle: userHandle,
            fromUserName: userName,
            fromUserAvatar: userAvatar,
            targetId: postId,
            targetType: 'post',
            message: 'curtiu seu post'
          });
        }
      }
    } catch (err) {
      console.error('Failed to create like notification:', err);
    }
  },

  /**
   * Unlike a post
   */
  async unlikePost(postId: string, userId: string): Promise<void> {
    const likeId = `${postId}_${userId}`;
    const likeRef = doc(db, 'likes', likeId);
    
    await deleteDoc(likeRef);

    // Decrement post likes count using distributed counter extension
    await counterService.incrementCounter(`posts/${postId}`, 'likesCount', -1);
  },

  /**
   * Check if user has liked a post
   */
  async hasUserLiked(postId: string, userId: string): Promise<boolean> {
    const likeId = `${postId}_${userId}`;
    const likeRef = doc(db, 'likes', likeId);
    const snapshot = await getDoc(likeRef);
    return snapshot.exists();
  },

  /**
   * Get all likes for a post
   */
  async getPostLikes(postId: string, maxLikes: number = 100): Promise<Like[]> {
    const q = query(
      collection(db, 'likes'),
      where('postId', '==', postId),
      orderBy('createdAt', 'desc'),
      limit(maxLikes)
    );

    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      createdAt: doc.data().createdAt?.toDate() || new Date()
    } as Like));
  },

  /**
   * Add a comment to a post
   */
  async addComment(
    postId: string, 
    userId: string, 
    userHandle: string,
    userName: string,
    content: string,
    userAvatar?: string
  ): Promise<string> {
    const commentRef = await addDoc(collection(db, 'comments'), {
      postId,
      userId,
      userHandle,
      userName,
      userAvatar,
      content,
      createdAt: Timestamp.now()
    });

    // Process mentions
    socialHelper.processMentions(
      content,
      userId,
      userHandle,
      userName,
      postId,
      'post',
      userAvatar
    ).catch(console.error);

    // Increment post comments count using distributed counter extension
    await counterService.incrementCounter(`posts/${postId}`, 'commentsCount', 1);

    // Create notification
    try {
      const post = await postService.getPostById(postId);
      if (post && post.authorId !== userHandle) {
        const authorProfile = await socialService.getProfileByHandle(post.authorId);
        if (authorProfile && authorProfile.userId) {
          await notificationService.createNotification(authorProfile.userId, {
            type: 'comment',
            fromUserId: userId,
            fromUserHandle: userHandle,
            fromUserName: userName,
            fromUserAvatar: userAvatar,
            targetId: postId,
            targetType: 'post',
            message: 'comentou no seu post'
          });
        }
      }
    } catch (err) {
      console.error('Failed to create comment notification:', err);
    }

    return commentRef.id;
  },

  /**
   * Get comments for a post
   */
  async getPostComments(postId: string, maxComments: number = 50): Promise<Comment[]> {
    const q = query(
      collection(db, 'comments'),
      where('postId', '==', postId),
      orderBy('createdAt', 'asc'),
      limit(maxComments)
    );

    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      createdAt: doc.data().createdAt?.toDate() || new Date()
    } as Comment));
  },

  /**
   * Delete a comment
   */
  async deleteComment(commentId: string, postId: string): Promise<void> {
    await deleteDoc(doc(db, 'comments', commentId));

    // Decrement post comments count using distributed counter extension
    await counterService.incrementCounter(`posts/${postId}`, 'commentsCount', -1);
  },

  /**
   * Save (Favorite) a post
   */
  async savePost(postId: string, userId: string, userHandle: string): Promise<void> {
    const saveId = `${postId}_${userId}`;
    const saveRef = doc(db, 'saves', saveId);
    
    await setDoc(saveRef, {
      postId,
      userId,
      userHandle,
      createdAt: Timestamp.now()
    });
  },

  /**
   * Unsave a post
   */
  async unsavePost(postId: string, userId: string): Promise<void> {
    const saveId = `${postId}_${userId}`;
    const saveRef = doc(db, 'saves', saveId);
    await deleteDoc(saveRef);
  },

  /**
   * Check if user has saved a post
   */
  async hasUserSaved(postId: string, userId: string): Promise<boolean> {
    const saveId = `${postId}_${userId}`;
    const saveRef = doc(db, 'saves', saveId);
    const snapshot = await getDoc(saveRef);
    return snapshot.exists();
  },

  /**
   * Get all saved posts for a user
   */
  async getUserSavedPosts(userId: string, maxPosts: number = 50): Promise<string[]> {
    const q = query(
      collection(db, 'saves'),
      where('userId', '==', userId),
      orderBy('createdAt', 'desc'),
      limit(maxPosts)
    );

    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => doc.data().postId);
  }
};
